import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { SxProps, Theme } from '@mui/material/styles';
import { ErrorBoundary } from '@sentry/react';
import { produce } from 'immer';
import React, { useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { Updater } from 'use-immer';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError, combineErrorsAndIssues } from '_lib/api/apiErrors';
import {
  AppointmentAvailabilityDTO,
  BlockTypeEnum,
  CompanyWindowLabelDisplayMap,
  ServiceTypeEnum,
  TradeEnum,
  RecurrenceTypeEnum,
  Company,
  ConflictError,
  ConflictTypeEnum,
  TimezoneAbbreviationEnum,
  CompanyWindowListDTO,
  AppointmentWindowListDTO,
  SetRequiredNonNullable
} from '_lib/api/apiTypes.shim';

import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Box } from '_lib/components/Box/Box';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';

import { useAlertDialog } from '_lib/hooks/AlertDialogHook';
import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';
import { useAppDispatch, useAppSelector } from '_lib/redux/hooks';
import { initAgentHoldAppointment } from '_lib/services/appointmentService';
import { minutesBeforeCutOff } from '_lib/services/appointmentWindowService';
import { PrimaryHeaderIconButton, PrimaryHeader, ContentBox, SimpleContentBox } from '_lib/styles/PhoneCall.styled';
import { createFilterWithChoices } from '_lib/types/filter.types';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';
import { toSxArray } from '_lib/utils/helpers';
import { logDebug } from '_lib/utils/logging';
import { reportException } from '_lib/utils/sentryService';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { CompanyContext, selectZoneZipContext } from '../../../../common/CompanyPane/Company.slice';
import {
  setAppointmentBookingPaneOpen,
  HeldAppointment,
  selectAppointmentBookingPaneOpen,
  selectPhoneCallContext,
  setHeldAppointmentContext,
  setPartialPhoneCallContext
} from '../../StApiPhoneCall.slice';

import { handledIssues } from '../CallDetailsPane/CallDetailsPane';

import { AppointmentBookingControls } from './AppointmentBookingControls/AppointmentBookingControls';
import { AppointmentBookingFilters } from './AppointmentBookingFilters/AppointmentBookingFilters';
import {
  AvailableWindow,
  BookingState,
  CompanyWindowsIdMap,
  FilterContext,
  WindowResponses,
  setSelectedKeyIfItExistsInFilterOptions
} from './AppointmentBookingPane.types';
import { createBookingState } from './AppointmentBookingPaneUtils';
import { AppointmentWindowList } from './AppointmentWindowList/AppointmentWindowList';

import type { OverrideProperties } from 'type-fest';

interface AppointmentBookingPaneProps {
  bookingState: BookingState;
  companyContext: CompanyContext;
  setBookingState: Updater<BookingState>;
  initializeBooking: React.RefObject<boolean>;
  sx?: SxProps<Theme>;
}

/**
 * NOTE - the AppointmentBookingPane is our only 'state-injected' pane, since it needs to be initialized in multiple spots!
 * @param props - AppointmentBookingPaneProps
 * @returns
 */
export const AppointmentBookingPane = (props: AppointmentBookingPaneProps): React.ReactElement => {
  const { bookingState, companyContext, setBookingState, initializeBooking, sx } = props;

  const api = useContext(ApiContext);

  const { enqueueSnackbar } = useOurSnackbar();

  const confirmationDialog = useConfirmationDialog();
  const alertDialog = useAlertDialog();

  /* -------- REDUX -------- */

  const zoneZipContext = useAppSelector(selectZoneZipContext);
  const phoneCallContext = useAppSelector(selectPhoneCallContext);
  const appointmentBookingPaneOpen = useAppSelector(selectAppointmentBookingPaneOpen);

  const dispatch = useAppDispatch();

  const handleCloseAppointmentWindows = () => {
    dispatch(setAppointmentBookingPaneOpen(false));
  };

  const handleAppointmentHeld = (heldAppointment: HeldAppointment) => {
    dispatch(setHeldAppointmentContext({ heldAppointment }));
    dispatch(setAppointmentBookingPaneOpen(false));
  };

  /* -------- END REDUX -------- */

  /* -------- FILTERS -------- */

  /**
   * Runs the provided filter context on the provided windows
   *
   * @param filterContext - provided filter context
   * @param availableWindows  - input windows
   */
  const runFilters = useCallback(
    (filterContext: FilterContext, availableWindows: AvailableWindow[]): AvailableWindow[] => {
      // only do Zone filtering if company is zoned and we have a zone.
      const selectedZoneId = (companyContext.company.isZoned && zoneZipContext?.selectedZone?.id) || undefined;
      const zoneTest = selectedZoneId
        ? (aw: AvailableWindow) => aw.companyWindow?.zones?.includes(selectedZoneId)
        : null;

      // If the selectedKey is anything other than '0' ...
      const filterTest = (aw: AvailableWindow) => {
        if (
          filterContext.serviceTypeFilter.selectedKey &&
          !aw.companyWindow.serviceTypes.includes(filterContext.serviceTypeFilter.selectedKey)
        )
          return false;

        if (
          filterContext.windowLabelFilter.selectedKey &&
          aw.companyWindow.label !== filterContext.windowLabelFilter.selectedKey
        )
          return false;

        if (
          filterContext.tradeFilter.selectedKey &&
          !aw.companyWindow.trades.includes(filterContext.tradeFilter.selectedKey)
        )
          return false;

        if (filterContext.warrantyFilter.selectedKey && !aw.companyWindow.isWarranty) return false;

        return true;
      };

      const filteredWindows = zoneTest
        ? availableWindows.filter((aw) => zoneTest(aw) && filterTest(aw))
        : availableWindows.filter(filterTest);

      // Sort filteredWindows by startTime ascending, and then blockType descending
      const sortedWindows = filteredWindows.sort((aw1, aw2) => {
        // If both all day, treat as same
        if (
          aw1.appointmentWindow.blockType === BlockTypeEnum.ALL_DAY &&
          aw2.appointmentWindow.blockType === BlockTypeEnum.ALL_DAY
        )
          return 0;

        // If one is all day and other is not, all day first
        if (
          aw1.appointmentWindow.blockType === BlockTypeEnum.ALL_DAY &&
          aw2.appointmentWindow.blockType !== BlockTypeEnum.ALL_DAY
        )
          return -1;
        if (
          aw2.appointmentWindow.blockType === BlockTypeEnum.ALL_DAY &&
          aw1.appointmentWindow.blockType !== BlockTypeEnum.ALL_DAY
        )
          return 1;

        // Get the AW start times if not ALL_DAY
        const firstStartTimeMillis = DateUtils.getMillisFromStartOfDay(
          aw1.appointmentWindow.startTime,
          companyContext.company.timezone
        );
        const secondStartTimeToMillis = DateUtils.getMillisFromStartOfDay(
          aw2.appointmentWindow.startTime,
          companyContext.company.timezone
        );

        // If start times are different, earliest first
        if (firstStartTimeMillis < secondStartTimeToMillis) return -1;
        if (firstStartTimeMillis > secondStartTimeToMillis) return 1;

        // If start times are same, specific time first
        return aw2.appointmentWindow.blockType - aw1.appointmentWindow.blockType;
      });

      return sortedWindows;
    },
    [companyContext.company, zoneZipContext?.selectedZone]
  );

  /**
   * Handles all filterContext updates
   * @param filterContext - updated filter context with options, selected key, etc
   */
  const handleFilterContextUpdate = useCallback(
    (filterContext: FilterContext) => {
      const tradeKey = filterContext.tradeFilter.selectedKey;
      const serviceTypeKey = filterContext.serviceTypeFilter.selectedKey;

      if (tradeKey !== phoneCallContext.trade || serviceTypeKey !== phoneCallContext.serviceType) {
        const clearIssues = [];
        if (tradeKey !== 0) clearIssues.push(handledIssues.trade);
        if (serviceTypeKey !== 0) clearIssues.push(handledIssues.serviceType);

        dispatch(
          setPartialPhoneCallContext({
            updates: {
              trade: tradeKey !== 0 ? tradeKey : undefined,
              serviceType: serviceTypeKey !== 0 ? serviceTypeKey : undefined
            },
            clearIssues
          })
        );
      }

      const availableWindows = [...bookingState.windowResponses.availableWindows];
      const sortedWindows = runFilters(filterContext, availableWindows);
      setBookingState((state) => {
        state.filterContext = filterContext;
        state.filteredWindows = sortedWindows;
      });
    },
    [
      bookingState.windowResponses.availableWindows,
      dispatch,
      phoneCallContext.serviceType,
      phoneCallContext.trade,
      runFilters,
      setBookingState
    ]
  );

  /**
   * Re-runs the filters with the context we currently have. This is needed when we change zone
   */
  const handleFilterRefresh = useCallback(() => {
    if (bookingState.filterContext && bookingState.windowResponses.availableWindows) {
      const sortedWindows = runFilters(bookingState.filterContext, bookingState.windowResponses.availableWindows);
      setBookingState((state) => {
        state.filteredWindows = sortedWindows;
      });
    }
  }, [bookingState.filterContext, bookingState.windowResponses.availableWindows, runFilters, setBookingState]);

  useEffect(() => {
    if (bookingState.forceFilterRefresh) {
      logDebug('AppointmentBookingPane - force filter refresh');
      handleFilterRefresh();
      setBookingState((state) => {
        state.forceFilterRefresh = false;
      });
    }
  }, [bookingState.forceFilterRefresh, handleFilterRefresh, setBookingState]);

  /**
   * Resets any non-readonly filters to key of 0, which displays '--'
   */
  const handleClearFilters = () => {
    if (bookingState.filterContext === null) return;

    const clearedFilterContext = produce(bookingState.filterContext, (draftState) => {
      if (!draftState.tradeFilter.isReadOnly) draftState.tradeFilter.selectedKey = 0;
      if (!draftState.serviceTypeFilter.isReadOnly) draftState.serviceTypeFilter.selectedKey = 0;
      if (!draftState.windowLabelFilter.isReadOnly) draftState.windowLabelFilter.selectedKey = 0;
      draftState.warrantyFilter.selectedKey = false;
    });

    handleFilterContextUpdate(clearedFilterContext);
  };

  const clearButtonDisabled = (): boolean => {
    // If we have no filter context, do not allow clear button to be clicked
    if (bookingState.filterContext === null) return true;

    // If any filters are non-readonly and set to a value other than the default ([0, '--']), do not disable the clear button.

    const tradeFilterSet: boolean =
      !bookingState.filterContext.tradeFilter.isReadOnly && bookingState.filterContext.tradeFilter.selectedKey !== 0;

    const serviceTypeFilterSet: boolean =
      !bookingState.filterContext.serviceTypeFilter.isReadOnly &&
      bookingState.filterContext.serviceTypeFilter.selectedKey !== 0;

    const windowLabelFilterSet: boolean =
      !bookingState.filterContext.windowLabelFilter.isReadOnly &&
      bookingState.filterContext.windowLabelFilter.selectedKey !== 0;

    const warrantyFilterSet: boolean = bookingState.filterContext.warrantyFilter.selectedKey === true;

    if (tradeFilterSet || serviceTypeFilterSet || windowLabelFilterSet || warrantyFilterSet) return false;

    // o.w. disable the clear button
    return true;
  };

  /**
   * Calculates which label filters should be visible based on the available companyWindows
   *
   * @param windowResponses - window responses object used to calculate visibility
   */
  const calcAvailableFilters = useCallback(
    (windowResponses: WindowResponses): FilterContext => {
      // get unique company window ids that have appointment windows
      const uniqueCompanyWindowIds = windowResponses.appointmentWindows
        .map((aw) => aw.companyWindowId)
        .filter((companyWindowId, index, array) => array.indexOf(companyWindowId) === index);

      // filter companyWindows to get companyWindows whose ids are are included in the list above
      const companyWindowsForAppointmentWindows = windowResponses.companyWindows.filter((companyWindow) => {
        return uniqueCompanyWindowIds.includes(companyWindow.id);
      });

      const uniqueCompanyWindowLabelKeys: number[] = companyWindowsForAppointmentWindows
        .map((cw) => cw.label)
        .filter((companyWindowLabel, index, array) => array.indexOf(companyWindowLabel) === index) // Note - this removes duplicates
        .sort((a, b) => a - b);

      const calculatedFilterContext: FilterContext = {
        windowLabelFilter: createFilterWithChoices(uniqueCompanyWindowLabelKeys, CompanyWindowLabelDisplayMap),
        tradeFilter: createFilterWithChoices(companyContext.company.callCenter.trades, TradeEnum),
        serviceTypeFilter: createFilterWithChoices(
          [ServiceTypeEnum.Repair, ServiceTypeEnum.Maintenance, ServiceTypeEnum.Estimate],
          ServiceTypeEnum
        ),
        warrantyFilter: { selectedKey: false }
      };

      return calculatedFilterContext;
    },
    [companyContext.company.callCenter.trades]
  );

  /* -------- END FILTERS -------- */

  /* -------- WINDOW CALCULATIONS -------- */

  /**
   * @param appointmentWindow
   * @param companyWindow
   * @param  remainingQuantity
   * @returns Available Window composed of appointmentWindow, companyWindow, remainingQuantity.
   */
  const buildAvailableWindow = (
    appointmentWindow: AppointmentWindowListDTO,
    companyWindowsIdMap: CompanyWindowsIdMap,
    remainingQuantity?: number
  ): AvailableWindow | null => {
    const companyWindow = companyWindowsIdMap[appointmentWindow.companyWindowId];
    if (!companyWindow) return null; // if AW referenced an inactive companyWindow, the AW is not valid anymore.
    return { appointmentWindow, companyWindow, remainingQuantity };
  };

  /**
   * @returns Array of CompanyWindows used by AppointmentWindows that are on the calendar starting from today
   */
  const getConfiguredCompanyWindows = (
    appointmentWindows: AppointmentWindowListDTO[],
    companyWindowsIdMap: CompanyWindowsIdMap
  ): CompanyWindowListDTO[] => {
    const companyWindowMap = new Map<string, CompanyWindowListDTO>();

    appointmentWindows.forEach((aw) => {
      const companyWindow = companyWindowsIdMap[aw.companyWindowId];
      if (companyWindow) companyWindowMap.set(companyWindow.id, companyWindow);
    });

    return Array.from(companyWindowMap.values());
  };

  /**
   * Fully functional-programmy version of old 'getAvailableWindows', returns 'availableWindow' list which combines some things
   * from the company window to make things easier to filter/display
   *
   * @param date - current date to find available windows for
   * @param companyWindows
   * @param appointmentWindows
   * @param appointments
   */
  const getAvailableWindows = useCallback(
    (
      date: Date,
      companyWindowsIdMap: CompanyWindowsIdMap,
      appointmentWindows: AppointmentWindowListDTO[],
      appointments: AppointmentAvailabilityDTO[],
      timezone: TimezoneAbbreviationEnum
    ): AvailableWindow[] => {
      const now = new Date();

      const dayOfWeek = DateUtils.getDayOfWeek(date, { timezone: companyContext.company.timezone });
      const dateOnlyString = DateUtils.toDateString(date, { timezone: companyContext.company.timezone });

      return appointmentWindows.reduce<AvailableWindow[]>((acc, appointmentWindow) => {
        if (new Date(appointmentWindow.startDate) > date) return acc;
        if (appointmentWindow.endDate !== null && new Date(appointmentWindow.endDate) < date) return acc;
        if (now > DateUtils.fromDateAndMinutesToDate(date, appointmentWindow.cutOffTimeMinutes, timezone)) return acc;
        if (appointmentWindow.recurrenceType !== RecurrenceTypeEnum.Never && !appointmentWindow.recurrenceId) {
          // if appointmentWindow is a recurring series (and not a Child) ...
          if (
            appointmentWindow.recurrenceType === RecurrenceTypeEnum.Weekly &&
            (!appointmentWindow.recurrenceDays || !appointmentWindow.recurrenceDays.some((v) => v === dayOfWeek))
          ) {
            return acc;
          }

          // if date is in exception list, then continue to next
          if (
            appointmentWindow.recurrenceExceptions &&
            appointmentWindow.recurrenceExceptions.some((aw) => aw.date === dateOnlyString)
          )
            return acc;
        }

        // count appointments that were booked for the current window & date
        const appointmentsForWindow = appointments.reduce((acc2, appointment) => {
          if (appointment.appointmentWindowId === appointmentWindow.id && appointment.windowDate === dateOnlyString)
            acc2 += 1;
          return acc2;
        }, 0);

        // we've exceeded the quantity for this window
        const remainingQuantity = appointmentWindow.quantity - appointmentsForWindow;
        if (remainingQuantity < 1) return acc;

        const availableWindow = buildAvailableWindow(appointmentWindow, companyWindowsIdMap, remainingQuantity);
        if (availableWindow) acc.push(availableWindow);

        return acc;
      }, []);
    },
    [companyContext.company.timezone]
  );

  /* -------- END WINDOW CALCULATIONS -------- */

  const reload = useCallback(
    async (company: Company, currentDate: Date | null) => {
      if (!currentDate) return;

      const windowDate = DateUtils.toDateString(currentDate, { timezone: company.timezone });

      try {
        setBookingState((state) => {
          state.isSaving = true;
          state.validationErrors = undefined;
          state.errorFetchingWindows = false;
        });
        const query = {
          companyId: company.id,
          callCenterId: company.callCenterId
        };
        const endDateNullOrMin = DateUtils.toStartOf(new Date(), 'day', { timezone: company.timezone }).toISOString();

        const [companyWindowResponse, appointmentWindowResponse, appointmentResponse] = await Promise.all([
          api.companyWindows.unpagedList({ params: { ...query }, disableErrorReporting: true }),
          api.appointmentWindows.unpagedList({ params: { ...query, endDateNullOrMin }, disableErrorReporting: true }),
          api.appointments.getAvailabilityAppointments({
            params: { ...query, windowDate },
            disableErrorReporting: true
          })
        ]);

        const companyWindowsIdMap = companyWindowResponse.results.reduce<CompanyWindowsIdMap>((acc, companyWindow) => {
          acc[companyWindow.id] = companyWindow;
          return acc;
        }, {});

        const availableWindows = getAvailableWindows(
          currentDate,
          companyWindowsIdMap,
          appointmentWindowResponse.results,
          appointmentResponse.results,
          company.timezone
        );

        const configuredCompanyWindows = getConfiguredCompanyWindows(
          appointmentWindowResponse.results,
          companyWindowsIdMap
        );

        const windowResponses: WindowResponses = {
          companyWindows: companyWindowResponse.results,
          configuredCompanyWindows,
          appointmentWindows: appointmentWindowResponse.results,
          appointments: appointmentResponse.results,
          companyWindowsIdMap,
          availableWindows
        };

        const availableFilters = calcAvailableFilters(windowResponses);

        /* If we have already selected filters, keep selections if the filter options have not changed */

        const existingTradeFilterKey = bookingState.filterContext?.tradeFilter.selectedKey;
        if (existingTradeFilterKey) {
          availableFilters.tradeFilter.selectedKey = existingTradeFilterKey;
        } else if (phoneCallContext.trade) {
          availableFilters.tradeFilter.selectedKey = phoneCallContext.trade;
        }

        // If a key is selected after reload, set the phoneCall trade
        if (
          availableFilters.tradeFilter.selectedKey &&
          availableFilters.tradeFilter.selectedKey !== phoneCallContext.trade
        ) {
          dispatch(
            setPartialPhoneCallContext({
              updates: { trade: availableFilters.tradeFilter.selectedKey },
              clearIssues: [handledIssues.trade]
            })
          );
        }

        const existingServiceTypeFilterKey = bookingState.filterContext?.serviceTypeFilter.selectedKey;
        if (existingServiceTypeFilterKey) {
          availableFilters.serviceTypeFilter.selectedKey = existingServiceTypeFilterKey;
        } else if (phoneCallContext.serviceType) {
          availableFilters.serviceTypeFilter.selectedKey = phoneCallContext.serviceType;
        }

        // If a key is selected after reload, set the phoneCall serviceType
        if (
          availableFilters.serviceTypeFilter.selectedKey &&
          availableFilters.serviceTypeFilter.selectedKey !== phoneCallContext.serviceType
        ) {
          dispatch(
            setPartialPhoneCallContext({
              updates: { serviceType: availableFilters.serviceTypeFilter.selectedKey },
              clearIssues: [handledIssues.serviceType]
            })
          );
        }

        const existingWindowLabelFilterKey = bookingState.filterContext?.windowLabelFilter.selectedKey;
        if (existingWindowLabelFilterKey) {
          setSelectedKeyIfItExistsInFilterOptions(existingWindowLabelFilterKey, availableFilters.windowLabelFilter);
        }

        if (bookingState.filterContext?.warrantyFilter.selectedKey) availableFilters.warrantyFilter.selectedKey = true;

        const sortedWindows = runFilters(availableFilters, availableWindows);

        setBookingState((state) => {
          state.windowResponses = windowResponses;
          state.filterContext = availableFilters;
          state.filteredWindows = sortedWindows;
        });
      } catch (err) {
        if (err instanceof BadRequestError) {
          // Stuff everything into nonFieldErrors, since we have no fields
          const combinedErrors = combineErrorsAndIssues(
            err.responseData?.nonFieldErrors,
            err.responseData?.fieldIssues
          );
          setBookingState((state) => {
            state.validationErrors = combinedErrors;
            state.filterContext = null;
            state.filteredWindows = [];
          });
        }
        setBookingState((state) => {
          state.errorFetchingWindows = true;
        });
        reportException(err);
      } finally {
        setBookingState((state) => {
          state.isSaving = false;
        });
      }
    },
    [
      api.appointmentWindows,
      api.appointments,
      api.companyWindows,
      bookingState.filterContext?.serviceTypeFilter.selectedKey,
      bookingState.filterContext?.tradeFilter.selectedKey,
      bookingState.filterContext?.warrantyFilter.selectedKey,
      bookingState.filterContext?.windowLabelFilter.selectedKey,
      calcAvailableFilters,
      dispatch,
      getAvailableWindows,
      phoneCallContext.serviceType,
      phoneCallContext.trade,
      runFilters,
      setBookingState
    ]
  );

  // HACK - Used when we want to pop dialog for holding appointment within 10 mins of booking end time... needs to be instant!
  const tempHeldAppointment = useRef<AvailableWindow | null>(null);

  /**
   * Disable and hide 'next available' unless we are searching 2 days away
   */
  const nextAvailableDisabled = (): boolean => {
    if (!bookingState.selectedDate) return true;
    const startOfToday = DateUtils.toStartOf(new Date(), 'day', { timezone: companyContext.company.timezone });
    const twoDaysFromToday = DateUtils.add(startOfToday, { days: 2 });
    return DateUtils.isBefore(bookingState.selectedDate, twoDaysFromToday);
  };

  /**
   * Changes the date to search for Appointment Availability
   *
   * @param {Date} newDate
   */
  const handleDateSelectChange = async (newDate: Date) => {
    const startOfNewDate = DateUtils.toStartOf(newDate, 'day', { timezone: companyContext.company.timezone });
    const startOfToday = DateUtils.toStartOf(new Date(), 'day', { timezone: companyContext.company.timezone });

    if (DateUtils.isBefore(startOfNewDate, startOfToday)) {
      enqueueSnackbar('Cannot look for appointments before today!');
      return;
    }

    try {
      setBookingState((state) => {
        state.selectedDate = newDate;
        state.isSaving = true;
      });

      const windowDate = DateUtils.toDateString(newDate, { timezone: companyContext.company.timezone });

      const appointmentResponse = await api.appointments.getAvailabilityAppointments({
        params: {
          companyId: companyContext.company.id,
          callCenterId: companyContext.company.callCenterId,
          windowDate
        },
        disableErrorReporting: true
      });

      const availableWindows = getAvailableWindows(
        newDate,
        bookingState.windowResponses.companyWindowsIdMap,
        bookingState.windowResponses.appointmentWindows,
        appointmentResponse.results,
        companyContext.company.timezone
      );

      const updatedWindowResponses: WindowResponses = {
        ...bookingState.windowResponses,
        appointments: appointmentResponse.results,
        availableWindows
      };

      setBookingState((state) => {
        state.windowResponses = updatedWindowResponses;
      });

      // NOTE - this should never be possible due to disabling the buttons without the filter context
      if (!bookingState.filterContext) throw new Error('Filter Context does not exist and is bugged on date change.');

      // Since we are not changing anything related to company windows, re-use existing filter state on new day
      const sortedWindows = runFilters(bookingState.filterContext, availableWindows);
      setBookingState((state) => {
        state.filteredWindows = sortedWindows;
      });
    } catch (err) {
      if (err instanceof BadRequestError) {
        // Stuff everything into nonFieldErrors, since we have no fields
        const combinedErrors = combineErrorsAndIssues(err.responseData?.nonFieldErrors, err.responseData?.fieldIssues);
        setBookingState((state) => {
          state.validationErrors = combinedErrors;
        });
      }
      reportException(err);
    } finally {
      setBookingState((state) => {
        state.isSaving = false;
      });
    }
  };

  const handleNextAvailable = () => {
    // Should never happen!
    if (!bookingState.selectedDate) return;

    // extra safe-guard in case clock ticks over midnight.. maybe we should toast?
    if (nextAvailableDisabled()) return;

    const allAvailableWindows = bookingState.windowResponses.appointmentWindows.reduce<AvailableWindow[]>((acc, aw) => {
      const availableWindow = buildAvailableWindow(aw, bookingState.windowResponses.companyWindowsIdMap);
      return availableWindow ? [...acc, availableWindow] : acc;
    }, []);

    const filteredWindows = bookingState.filterContext
      ? runFilters(bookingState.filterContext, allAvailableWindows)
      : allAvailableWindows;
    if (filteredWindows.length === 0) {
      enqueueSnackbar('No appointment windows match currently selected filters!');
      return;
    }

    // now check to see if windows matching filters have "next availability" after current date
    const currentSearchDate = DateUtils.toDateString(bookingState.selectedDate, {
      timezone: companyContext.company.timezone
    });

    type AvailableWindowWithNextAvailableDate = OverrideProperties<
      AvailableWindow,
      { appointmentWindow: SetRequiredNonNullable<AvailableWindow['appointmentWindow'], 'nextAvailableDate'> }
    >;

    const windowsWithAvailability = filteredWindows.filter(
      (fw) =>
        fw.appointmentWindow.nextAvailableQuantity > 0 &&
        fw.appointmentWindow.nextAvailableDate &&
        fw.appointmentWindow.nextAvailableDate > currentSearchDate
    ) as AvailableWindowWithNextAvailableDate[];

    const firstMatch =
      windowsWithAvailability.length &&
      windowsWithAvailability.sort((a, b) => {
        if (a.appointmentWindow.nextAvailableDate < b.appointmentWindow.nextAvailableDate) return -1;
        if (a.appointmentWindow.nextAvailableDate > b.appointmentWindow.nextAvailableDate) return 1;
        return 0;
      })[0];

    if (!firstMatch) {
      // TODO: better messaging/display .. not having "next available" doesn't mean there is NO availability
      //       since we only track the first date of next availability
      enqueueSnackbar(`No appointment windows have a next availability after: ${currentSearchDate}`);
      return;
    }

    handleDateSelectChange(
      DateUtils.fromDateStringToNoonDate(firstMatch.appointmentWindow.nextAvailableDate, {
        timezone: companyContext.company.timezone
      })
    );
  };

  const handleDateSelectIncrement = () => {
    if (bookingState.selectedDate) handleDateSelectChange(DateUtils.add(bookingState.selectedDate, { days: 1 }));
  };

  const handleDateSelectDecrement = () => {
    if (bookingState.selectedDate) handleDateSelectChange(DateUtils.add(bookingState.selectedDate, { days: -1 }));
  };

  /**
   * Pops reload dialog
   */
  const handleRefreshAppointmentWindows = async () => {
    const promise = await confirmationDialog.show({
      titleText: 'CONFIRM RELOAD',
      contentText:
        'Please be aware that frequent refreshing can cause a significant burden on the system. Would you like to continue?',
      confirmButtonText: 'Reload'
    });

    if (promise?.action === 'CONFIRM') reload(companyContext.company, bookingState.selectedDate);
  };

  /**
   * Pops an alert to the user telling them about the specific issue with the conflict and on "Ok"/Exit of alert
   * it will reload the booking resources so the User can book again with the latest data.
   * @param  conflictError - Validation ConflictError object containing an `errorMessage` for display
   */
  const handleAppointmentWindowConflict = async (conflictError: ConflictError) => {
    await alertDialog.show({ variant: 'error', titleText: 'WINDOW CONFLICT', content: conflictError.errorMessage });
    reload(companyContext.company, bookingState.selectedDate);
  };

  /**
   * Helper function used to help with interrupting dialog when holding appointment
   *
   * NOTE - this function will handle saving/error response stuff, do not need to catch errors
   *
   * @param availableWindow - appointmentWindow and companyWindow used to set appointment
   * @returns
   */
  const holdAppointment = async (availableWindow: AvailableWindow) => {
    if (!bookingState.selectedDate) return;

    const newAppointment = initAgentHoldAppointment(
      bookingState.selectedDate,
      companyContext.company,
      availableWindow.appointmentWindow,
      availableWindow.companyWindow
    );

    try {
      setBookingState((state) => {
        state.isSaving = true;
      });

      const appointment = await api.appointments.create(newAppointment);

      handleAppointmentHeld({
        appointment,
        appointmentWindow: availableWindow.appointmentWindow,
        companyWindow: availableWindow.companyWindow,
        tradeFromFilter: bookingState.filterContext?.tradeFilter.selectedKey,
        serviceTypeFromFilter: bookingState.filterContext?.serviceTypeFilter.selectedKey
      });
    } catch (err) {
      if (err instanceof BadRequestError) {
        /*
            If we have conflict errors then handling them takes priority over all other errors

            In this case we explicitly look for an AppointmentWindow conflict and if it occurred, we reload
            booking resources and display a message allowing them to complete booking with fresh data

            This conflict can occur when the Appointment Window selected for holding the appointment is
            modified by some one on the Schedule such that:
            - The window being booked by the agent was a recurring parent window
            - Someone performed a "this event" change for the same date our agent was booking after our agent
            loaded the windows to be booked (aka potential for stale Appointment Window state)
            - The change creates a recurrenceException and a new child window if schedule user performed an
            edit this event action
            - This change creates a recurrenceException if schedule user performed a delete this event
          */

        if (err.responseData?.conflictErrors?.length) {
          const awConflictError = err.responseData.conflictErrors.find(
            (ce) => ce.conflictType === ConflictTypeEnum.APPOINTMENT_WINDOW_PARENT
          );
          if (awConflictError) return handleAppointmentWindowConflict(awConflictError);
        }

        if (err.responseData?.nonFieldErrors?.length || err.responseData?.fieldIssues?.length) {
          const combinedErrors = combineErrorsAndIssues(
            err.responseData?.nonFieldErrors,
            err.responseData?.fieldIssues
          );
          setBookingState((state) => {
            state.validationErrors = combinedErrors;
          });
        } else {
          // not a validation error, then something unexpected happened
          enqueueSnackbar('Unexpected error occurred when holding appointment.');
          reportException(new Error('Unexpected error occurred when holding appointment'), {
            extra: {
              availableWindow,
              newAppointment,
              responseData: err.responseData,
              axiosErrorCode: err.axiosError.code,
              axiosErrorStatus: err.axiosError.response?.status
            }
          });
        }
      }
      reportException(err, { extra: { availableWindow, newAppointment } });
    } finally {
      setBookingState((state) => {
        state.isSaving = false;
      });
    }
  };

  /**
   * Initializes an Appointment for the provided Available Window.
   * Then Creates the Appointment and communicates the Appointment and the
   * AppointmentWindow it was created for back up to redux.
   *
   * @param availableWindow - window to be created with status of AGENT_HOLD
   */
  const handleAppointmentAgentHold = async (availableWindow: AvailableWindow) => {
    if (!bookingState.selectedDate) return;

    setBookingState((state) => {
      state.validationErrors = undefined;
    });
    const appointmentWindow = availableWindow.appointmentWindow;
    const minsBeforeCutOff = minutesBeforeCutOff(
      appointmentWindow,
      bookingState.selectedDate,
      companyContext.company.timezone
    );

    if (minsBeforeCutOff <= 0) {
      setBookingState((state) => {
        state.validationErrors = ['Unable to save this appointment. It is now after the cut off time.'];
      });
      enqueueSnackbar('Unable to save this appointment. It is now after the cut off time.');
      return;
    } else if (minsBeforeCutOff <= 10) {
      const cutOffTime = DateUtils.fromDateAndMinutesToDate(
        bookingState.selectedDate,
        appointmentWindow.cutOffTimeMinutes,
        companyContext.company.timezone
      );
      const cutOffTimePretty = DateUtils.format(cutOffTime, DateFormatEnum.DateAtTime, {
        timezone: companyContext.company.timezone,
        showTimezone: true
      });

      const confirmContent = `The cut off time for booking is ${cutOffTimePretty} or in approximately ${Math.floor(
        minsBeforeCutOff
      )} minutes`;

      tempHeldAppointment.current = availableWindow;

      const promise = await confirmationDialog.show({
        titleText: 'CONFIRM CUT OFF TIME',
        contentText: confirmContent,
        confirmButtonText: 'Hold Anyways'
      });

      if (promise?.action === 'CONFIRM' && tempHeldAppointment.current) holdAppointment(tempHeldAppointment.current);
      tempHeldAppointment.current = null;

      return;
    }

    // This function will handle any errors it throws internally, no need to await
    holdAppointment(availableWindow);
  };

  const initAppointmentBookingPane = useCallback(
    async (company: Company) => {
      const selectedDay = phoneCallContext.appointmentDateTime
        ? new Date(phoneCallContext.appointmentDateTime)
        : new Date();

      const selectedNoonDate = DateUtils.toNoonDate(selectedDay, { timezone: company.timezone });

      setBookingState(createBookingState({ companyId: company.id, selectedDate: selectedNoonDate }));

      await reload(company, selectedNoonDate);
    },
    [phoneCallContext.appointmentDateTime, reload, setBookingState]
  );

  // Re-run filters when we change zone
  useEffect(() => {
    // NOTE - this always runs first when component is rendered,
    //        might we want to guard this when we know initialization is occurring?
    handleFilterRefresh();
  }, [zoneZipContext?.selectedZipOrPostal, zoneZipContext?.selectedZone, handleFilterRefresh]);

  // Initialization useEffect
  useEffect(() => {
    if (appointmentBookingPaneOpen && companyContext.company && initializeBooking.current) {
      initializeBooking.current = false;

      initAppointmentBookingPane(companyContext.company);
    }
  }, [appointmentBookingPaneOpen, companyContext.company, initializeBooking, initAppointmentBookingPane]);

  const windowsExistForTradeAndServiceType = useMemo(() => {
    if (!bookingState.filterContext) return false;

    const tradeKey = bookingState.filterContext.tradeFilter.selectedKey;
    const serviceTypeKey = bookingState.filterContext.serviceTypeFilter.selectedKey;

    if (!tradeKey || !serviceTypeKey) return false;

    const filteredWindowsExist = bookingState.windowResponses.configuredCompanyWindows.some((companyWindow) => {
      if (!companyWindow.trades.includes(tradeKey)) return false;
      if (!companyWindow.serviceTypes.includes(serviceTypeKey)) return false;
      return true;
    });

    return filteredWindowsExist;
  }, [bookingState]);

  const getAppointmentBookingList = () => {
    if (!bookingState.filterContext) return <></>;

    if (
      (bookingState.filterContext.tradeFilter.selectedKey === 0 ||
        bookingState.filterContext.serviceTypeFilter.selectedKey === 0) &&
      !bookingState.isSaving
    ) {
      return (
        <Box
          sx={(theme) => ({
            padding: 2,
            color: theme.palette.error.contrastText,
            bgcolor: theme.palette.error.main,
            borderRadius: 1,
            margin: 1
          })}>
          <Typography text='Trade and Service Type must be selected.' align='center' />
        </Box>
      );
    }

    if (
      bookingState.filterContext.tradeFilter.selectedKey &&
      bookingState.filterContext.serviceTypeFilter.selectedKey &&
      !windowsExistForTradeAndServiceType &&
      !bookingState.isSaving
    ) {
      return (
        <Box
          sx={(theme) => ({
            padding: 2,
            color: theme.palette.error.contrastText,
            bgcolor: theme.palette.error.main,
            borderRadius: 1,
            margin: 1
          })}>
          <Typography>
            {'Appointment Windows are not configured for the selected Trade and Service Type: '}
            <span
              style={{
                fontWeight: 'bold'
              }}>{`${TradeEnum[bookingState.filterContext.tradeFilter.selectedKey]} - ${ServiceTypeEnum[bookingState.filterContext.serviceTypeFilter.selectedKey]}.`}</span>
            {' Please check Dispatch Rules'}
          </Typography>
        </Box>
      );
    }

    return (
      <AppointmentWindowList
        timezone={companyContext.company.timezone}
        isManualEmployeeAssignEnabled={companyContext.company.isManualEmployeeAssignEnabled}
        filteredWindows={bookingState.filteredWindows}
        isLoading={bookingState.isSaving}
        onAppointmentAgentHold={handleAppointmentAgentHold}
      />
    );
  };

  return (
    <Box
      sx={[
        {
          display: 'flex',
          flexDirection: 'column'
        },
        ...toSxArray(sx)
      ]}>
      <ErrorBoundary
        beforeCapture={(scope) => {
          scope.setTag('pane', 'Appointment Booking Pane');
        }}
        fallback={({ resetError }) => (
          <ErrorBoundaryFallback
            title='Error Displaying Appointment Booking'
            onRefresh={() => {
              initAppointmentBookingPane(companyContext.company);
              resetError();
            }}
          />
        )}>
        <PrimaryHeader>
          <Stack direction='row' spacing={2} sx={{ paddingY: 0.25, alignItems: 'center' }}>
            <PrimaryHeaderIconButton onClick={() => handleCloseAppointmentWindows()}>
              <ArrowBackIcon />
            </PrimaryHeaderIconButton>
            <Typography variant='h3' text='APPOINTMENT WINDOWS' />
          </Stack>
          <Divider />
        </PrimaryHeader>
        <SimpleContentBox>
          <BackdropSpinner open={bookingState.isSaving} />
          <AppointmentBookingFilters
            filterContext={bookingState.filterContext}
            onFilterContextUpdate={handleFilterContextUpdate}
          />
          <Divider />
          <AppointmentBookingControls
            selectedDate={bookingState.selectedDate}
            bookingControlsDisabled={
              bookingState.errorFetchingWindows ||
              !bookingState.filterContext ||
              !bookingState.filterContext.tradeFilter.selectedKey ||
              !bookingState.filterContext.serviceTypeFilter.selectedKey ||
              !windowsExistForTradeAndServiceType
            }
            clearButtonDisabled={clearButtonDisabled}
            nextAvailableDisabled={nextAvailableDisabled}
            onDateSelectDecrement={handleDateSelectDecrement}
            onDateSelectChange={handleDateSelectChange}
            onDateSelectIncrement={handleDateSelectIncrement}
            onNextAvailable={handleNextAvailable}
            onRefreshAppointmentWindows={handleRefreshAppointmentWindows}
            onClearFilters={handleClearFilters}
            timezone={companyContext.company.timezone}
          />
          <Divider />
        </SimpleContentBox>
        <Box sx={{ height: '100%', position: 'relative' }}>
          <ContentBox>
            {bookingState.validationErrors?.length && (
              <ErrorDisplay errors={bookingState.validationErrors} sx={{ borderRadius: 1, margin: 1 }} />
            )}
            {!bookingState.errorFetchingWindows && getAppointmentBookingList()}
            {bookingState.errorFetchingWindows && (
              <ErrorBoundaryFallback
                sx={{ position: 'relative', justifyContent: 'flex-start', paddingTop: 2 }}
                title='Error Fetching Appointment Windows.'
                onRefresh={() => reload(companyContext.company, bookingState.selectedDate)}
              />
            )}
          </ContentBox>
        </Box>
        {confirmationDialog.element}
        {alertDialog.element}
      </ErrorBoundary>
    </Box>
  );
};
